import { createSelector } from '@reduxjs/toolkit';
import { denormalize } from 'normalizr';
import * as schemas from '../schemas';
import { list } from '~/tools/list';
import { baseEmployeeAttrIds } from '~/config/settings/attribute.settings';
import { keys as attributeTypeKeys } from '~/config/settings/attribute_types.settings';

const memoizedEmployeeAttribute = createSelector(
  (state) => state.entities.employeeAttributes,
  (state, employeeAttributeId) => employeeAttributeId,
  (employeeAttributes, employeeAttributeId) => {
    return denormalize(employeeAttributeId, schemas.employeeAttribute, {
      employeeAttributes,
    });
  },
);

const selectEmployeeAttribute = (employeeAttributeId) => (state) => {
  return memoizedEmployeeAttribute(state, employeeAttributeId);
};

const memoizedEmployeeAttributes = createSelector(
  (state) => state.entities.companies,
  (state) => state.entities.employeeAttributes,
  (state, companyId) => companyId,
  (companies, employeeAttributes, companyId) => {
    if (Object.keys(employeeAttributes).length === 0) return [];
    const company = denormalize(companyId, schemas.company, {
      companies,
      employeeAttributes,
    });
    if (!company) return [];

    return company.employeeAttributes || [];
  },
);

const selectEmployeeAttributes = (companyId) => (state) => {
  return memoizedEmployeeAttributes(state, companyId);
};

const memoizedEmployeeAttributesDict = createSelector(
  (state) => state.entities.employeeAttributes,
  (state) => state.permissions.surveyTypes,
  (state, options) => options,
  (employeeAttributesDict, surveyTypePermissions, options = {}) => {
    const {
      // @ts-expect-error TS(2339): Property 'surveyTypeId' does not exist on type '{}... Remove this comment to see the full error message
      surveyTypeId = null,
      // @ts-expect-error TS(2339): Property 'includePeopleLeaderAttribute' does not e... Remove this comment to see the full error message
      includePeopleLeaderAttribute = false,
      // @ts-expect-error TS(2339): Property 'includeBaseAttributes' does not exist on... Remove this comment to see the full error message
      includeBaseAttributes = false,
      // @ts-expect-error TS(2339): Property 'includePrivateAttributes' does not exist... Remove this comment to see the full error message
      includePrivateAttributes = false,
      includeArchivedAttributes = false,
    } = options;
    const employeeAttributes = Object.values(employeeAttributesDict);
    const surveyTypePermission = surveyTypePermissions[surveyTypeId];

    const filteredAttributesDict = employeeAttributes.reduce((acc, attr) => {
      const isSurveyTypeAttribute = Boolean(
        surveyTypePermission?.filterAttributes?.length === 0 ||
          // @ts-expect-error TS(2571): Object is of type 'unknown'.
          surveyTypePermission?.filterAttributes?.includes(attr.id),
      );
      // @ts-expect-error TS(2571): Object is of type 'unknown'.
      const isBaseAttribute = !!attr.baseAttributeId;
      const isPeopleLeaderAttribute =
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        attr.baseAttributeId === baseEmployeeAttrIds.MANAGER_EMPLOYEE_ID;

      // Don't include attributes that are not accessible by user's survey type
      // permissions *if* selecting based on a specific `surveyTypeId`.
      if (surveyTypeId && !isSurveyTypeAttribute) {
        return acc;
      }

      // Ignore private attributes.
      // @ts-expect-error TS(2571): Object is of type 'unknown'.
      if (!includePrivateAttributes && attr.isPrivate) {
        return acc;
      }

      if (!includeArchivedAttributes && attr.isArchived) {
        return acc;
      }

      // Ignore base attributes.
      if (!includeBaseAttributes && isBaseAttribute && !includePeopleLeaderAttribute) {
        return acc;
      }

      // Ignore base attributes that are not people leader attributes when
      // including people leaders.
      if (!includeBaseAttributes && isBaseAttribute && includePeopleLeaderAttribute && !isPeopleLeaderAttribute) {
        return acc;
      }

      // Ignore manage employee id attribute if it is not explicitly included.
      if (!includePeopleLeaderAttribute && isPeopleLeaderAttribute) {
        return acc;
      }

      return {
        // @ts-expect-error TS(2698): Spread types may only be created from object types... Remove this comment to see the full error message
        ...acc,
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        [attr.id]: attr,
      };
    }, {});

    return filteredAttributesDict;
  },
);

// TODO: memoize this
const selectEmployeeAttributesDict = (options) => (state) => memoizedEmployeeAttributesDict(state, options);

const selectPeopleLeaderAttribute = (companyId) => (state) => {
  const employeeAttributes = memoizedEmployeeAttributes(state, companyId);
  const peopleLeaderAttribute = employeeAttributes.find(list.peopleLeader);

  return peopleLeaderAttribute;
};

const selectManagerEmployeeIdAttrId = (companyId) => (state) => {
  const managerEmployeeAttribute = selectPeopleLeaderAttribute(companyId)(state);

  return managerEmployeeAttribute?.id || null;
};

const selectCompanyEmployeeIdAttrId = (companyId) => (state) => {
  const employeeAttributes = memoizedEmployeeAttributes(state, companyId);
  const companyEmployeeIdAttribute = employeeAttributes.find(
    (attr) => attr.baseAttributeId === baseEmployeeAttrIds.COMPANY_EMPLOYEE_ID,
  );
  return companyEmployeeIdAttribute?.id || null;
};

const selectPrimaryAttributes = (companyId) => (state) => {
  const employeeAttributes = memoizedEmployeeAttributes(state, companyId);
  return employeeAttributes.filter((attr) => attr.isPrimary);
};

const memoizedCompanyTextAttributes = createSelector(memoizedEmployeeAttributes, (employeeAttributes) => {
  return employeeAttributes.filter((attr) => attr.type.toLowerCase() === attributeTypeKeys.STRING.toLowerCase());
});

const selectCompanyTextAttributes = (companyId) => (state) => memoizedCompanyTextAttributes(state, companyId);

const memoizedCompanyDateAttributes = createSelector(memoizedEmployeeAttributes, (employeeAttributes) => {
  return employeeAttributes.filter((attr) => attr.type.toLowerCase() === attributeTypeKeys.DATE.toLowerCase());
});

const selectCompanyDateAttributes = (companyId) => (state) => memoizedCompanyDateAttributes(state, companyId);

export const employeeAttributeSelectors = {
  selectCompanyEmployeeIdAttrId,
  selectEmployeeAttribute,
  selectEmployeeAttributes,
  selectEmployeeAttributesDict,
  selectManagerEmployeeIdAttrId,
  selectPeopleLeaderAttribute,
  selectPrimaryAttributes,
  selectCompanyTextAttributes,
  selectCompanyDateAttributes,
};
